import { TermParagraph } from '@core/model/domain/termParagraph';
import { Result } from '@core/model/framework/Result/Result';
import { GetTerms } from '@core/usecase/getTerms';
import { SendConsent } from '@core/usecase/sendConsent';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import frenchi18n from '@assets/i18n/fr.json';

export type ConsentTermParagraph = {
  nonUnderlinedText: string;
  underlinedText: string;
  consentText: string;
  title: string;
};

export class ConsentVM {
  constructor(
    private storeService: StoreService,
    private processEventService: ProcessEventService,
    private tracker: TrackerService,
    private getTerms: GetTerms,
    private sendConsent: SendConsent
  ) {}

  async onInit(): Promise<{ cguLink: string; termParagraphs: TermParagraph[] }> {
    this.tracker.trackPage({ taggingPage: 'CONSENT' });

    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');

    const terms = Result.unwrap(
      await this.getTerms.execute({
        userToken,
      })
    );

    const termParagraphs = JSON.parse(terms.jsonFile) as TermParagraph[];
    const cguLink = terms.pdfURI;
    this.storeService.setVariable('CGU_VERSION', terms.cguVersion);
    this.storeService.setVariable('TERMS_VERSION', terms.termsVersion);

    return {
      cguLink,
      termParagraphs,
    };
  }

  async onAccepted() {
    this.tracker.trackClick({ click: frenchi18n.i_accept, taggingPage: 'CONSENT' });

    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');
    const userId = this.storeService.getVariableOrThrow('USER_ID');
    const hasConsented = true;
    const cguVersion = this.storeService.getVariableOrThrow('CGU_VERSION');
    const termsVersion = this.storeService.getVariableOrThrow('TERMS_VERSION');

    Result.unwrap(
      await this.sendConsent.execute({
        userToken,
        userId,
        hasConsented,
        termsVersion,
        cguVersion,
      })
    );

    this.processEventService.push(ProcessEventFactory.SpawnConsentEvent());
  }

  mapToConsentTermParagraph(termParagraphs: TermParagraph[]): ConsentTermParagraph[] {
    return termParagraphs.map((term) => {
      const [nonUnderlinedText, ...underlinedTexts] = term.title.split(' ');
      const underlinedTextJoined = underlinedTexts.join(' ').trim();

      return {
        nonUnderlinedText: nonUnderlinedText!,
        underlinedText: underlinedTextJoined,
        consentText: term.consentText,
        title: term.title,
      };
    });
  }

  mapToTermParagraph(consentTermParagraph: ConsentTermParagraph): TermParagraph {
    return {
      title: consentTermParagraph.title,
      consentText: consentTermParagraph.consentText,
      description: consentTermParagraph.nonUnderlinedText + consentTermParagraph.underlinedText,
    };
  }
}
